import { setPlanningShiftTableLoading } from '@/entities/gridTables/slices/planningShiftTablesSlice'
import { setPlanningUnassignedTableLoading } from '@/entities/gridTables/slices/planningUnassignedTablesSlice'
import { setRunsheetShiftTableLoading } from '@/entities/gridTables/slices/runsheetShiftTablesSlice'
import { setRunsheetUnassignedTableLoading } from '@/entities/gridTables/slices/runsheetUnassignedTablesSlice'
import { processAssignToRunsheet } from '@/entities/gridTables/thunks/processAssignToRunsheet'
import { processAssignToScenario } from '@/entities/gridTables/thunks/processAssignToScenario'
import { processDropOnInbox } from '@/entities/gridTables/thunks/processDropOnInbox'
import { processDropOnShift, ProcessDropOnShiftPayload } from '@/entities/gridTables/thunks/processDropOnShift'
import { processUnassignOrder } from '@/entities/gridTables/thunks/processUnassignOrders'
import { updatePlanningShiftTableState } from '@/entities/gridTables/thunks/updatePlanningShiftTableState'
import { updatePlanningUnassignedTableState } from '@/entities/gridTables/thunks/updatePlanningUnassignedTableState'
import { updateRunsheetShiftTableState } from '@/entities/gridTables/thunks/updateRunsheetShiftTableState'
import { updateRunsheetUnassignedTableState } from '@/entities/gridTables/thunks/updateRunsheetUnassignedTableState'
import { AssignedToScenario } from '@/entities/orders/models/Order'
import { fetchRunsheets, fetchScenarios } from '@/entities/schedules'
import { CurrentScreenType } from '@/entities/userInterface/model/CurrentScreenType'
import { GridTableDragItem, GridTableDragType } from '@/shared/components/GridTable/models/GridTableDragType'
import { TREE_WIDGETS } from '@/shared/components/Mosaic/models/widgetType'
import { useAppDispatch } from '@/shared/store'
import { EntityId } from '@reduxjs/toolkit'

import { CustomTreeItemType } from '../model/CustomTreeItemType'

export type ProcessDropOnTreeItemArgs = {
  dispatch: ReturnType<typeof useAppDispatch>
  item: GridTableDragItem
  treeItem: CustomTreeItemType
  currentScreen: CurrentScreenType
}

export const processDropOnTreeItem = async (args: ProcessDropOnTreeItemArgs) => {
  const { item, treeItem } = args

  if (item.rows.length === 0) return

  if (treeItem.widgetType === TREE_WIDGETS.Inbox) {
    processPlanningDropOnInboxTreeItem(args)
  } else if (treeItem.widgetType === TREE_WIDGETS.ScenarioUnassignedOrders) {
    // Ensure that the unassigned orders are not dropped on the same scenario
    if (item.type === GridTableDragType.UNASSIGNED_ORDER_ROW &&
      item.rows[0].scenarioId == treeItem.scenarioId) return

    processPlanningDropOnUnassignedTreeItem(args)
  } else if (treeItem.widgetType === TREE_WIDGETS.RunsheetUnassignedOrders) {
    // Ensure that the unassigned orders are not dropped on the same scenario
    if (item.type === GridTableDragType.UNASSIGNED_ORDER_ROW &&
      item.rows[0].runsheetId == treeItem.runsheetId) return

    processRunsheetDropOnUnassignedTreeItem(args)
  } else if (treeItem.widgetType === TREE_WIDGETS.PlanningShiftTable) {
    // Ensure that the stops are not dropped on the same shift
    if (item.type === GridTableDragType.SHIFT_STOP_ROW &&
      item.rows[0].shiftId == treeItem.shiftId) return

    processPlanningDropOnShiftTreeItem(args)
  } else if (treeItem.widgetType === TREE_WIDGETS.RunsheetShiftTable) {
    // Ensure that the stops are not dropped on the same shift
    if (item.type === GridTableDragType.SHIFT_STOP_ROW &&
      item.rows[0].shiftId == treeItem.shiftId) return

    processRunsheetDropOnShiftTreeItem(args)
  }
}

export const processPlanningDropOnInboxTreeItem = async ({ dispatch, item }: ProcessDropOnTreeItemArgs) => {
  const orderIds = item.rows.map((stop) => stop.orderId)
  const shiftId = item.rows[0].shiftId
  const scenarioId = item.rows[0].scenarioId

  if (!scenarioId) return

  // There is only one inbox, so no need to check targetId
  if (item.type === GridTableDragType.SHIFT_STOP_ROW) {
    dispatch(setPlanningShiftTableLoading({ id: shiftId, isLoading: true }))

    await dispatch(processDropOnInbox({ scenarioId, orderIds }))
    await dispatch(fetchScenarios({ scenarioIds: [scenarioId] }))
    await dispatch(updatePlanningShiftTableState({ shiftId: shiftId }))

    dispatch(setPlanningShiftTableLoading({ id: shiftId, isLoading: false }))
  } else if (item.type === GridTableDragType.UNASSIGNED_ORDER_ROW) {
    dispatch(setPlanningUnassignedTableLoading({ id: scenarioId, isLoading: true }))

    await dispatch(processDropOnInbox({ scenarioId, orderIds }))
    await dispatch(fetchScenarios({ scenarioIds: [scenarioId] }))
    await dispatch(updatePlanningUnassignedTableState({ scenarioId: scenarioId }))

    dispatch(setPlanningUnassignedTableLoading({ id: scenarioId, isLoading: false }))
  }
}

export const processPlanningDropOnUnassignedTreeItem = async ({ dispatch, item, treeItem }: ProcessDropOnTreeItemArgs) => {
  if (treeItem.widgetType !== TREE_WIDGETS.ScenarioUnassignedOrders) return

  const scenarioId = treeItem.scenarioId

  if (!scenarioId) return

  if (item.type === GridTableDragType.SHIFT_STOP_ROW) {
    // Receive orders from shift
    const orderIds = item.rows.map((stop) => stop.orderId)
    const sourceScenarioId = item.rows[0].scenarioId
    const isSameScenario = sourceScenarioId == scenarioId
    const shiftId = item.rows[0].shiftId

    // Set Loading State
    dispatch(setPlanningShiftTableLoading({ id: shiftId, isLoading: true }))

    // Process Drop
    if (isSameScenario) {
      dispatch(setPlanningUnassignedTableLoading({ id: scenarioId, isLoading: true }))
      await dispatch(processUnassignOrder({ shiftId, orderIds }))
    } else {
      await dispatch(processAssignToScenario({ scenarioId, orderIds }))
    }

    // Fetch scenarios and update table data
    await dispatch(fetchScenarios({ scenarioIds: [scenarioId] }))
    await Promise.allSettled([
      dispatch(updatePlanningShiftTableState({ shiftId: shiftId })),
      dispatch(updatePlanningUnassignedTableState({ scenarioId: scenarioId })),
    ])

    // Reset Loading State
    dispatch(setPlanningShiftTableLoading({ id: shiftId, isLoading: false }))
    dispatch(setPlanningUnassignedTableLoading({ id: scenarioId, isLoading: false }))
  } else if (item.type === GridTableDragType.INBOX_ORDER_ROW || item.type === GridTableDragType.UNASSIGNED_ORDER_ROW) {
    const orderIds = item.rows.map((row) => row.orderId)

    // Set Loading State
    dispatch(setPlanningUnassignedTableLoading({ id: scenarioId, isLoading: true }))

    await dispatch(processAssignToScenario({ scenarioId, orderIds }))
    await dispatch(fetchScenarios({ scenarioIds: [scenarioId] }))
    await Promise.allSettled([
      dispatch(updatePlanningUnassignedTableState({ scenarioId: scenarioId })),
    ])

    // Reset Loading State
    dispatch(setPlanningUnassignedTableLoading({ id: scenarioId, isLoading: false }))
  }
}

export const processPlanningDropOnShiftTreeItem = async ({ dispatch, item, treeItem }: ProcessDropOnTreeItemArgs) => {
  if (treeItem.widgetType !== TREE_WIDGETS.PlanningShiftTable) return

  const sourceRowData = item.rows
  const sourceType = sourceRowData[0]?.dragType
  const sourceShiftId = sourceRowData[0].shiftId
  const sourceScenarioId = sourceRowData[0].scenarioId
  const targetShiftId = treeItem.shiftId
  const targetScenarioId = treeItem.scenarioId
  const refreshUnassignedOrders = sourceType === GridTableDragType.UNASSIGNED_ORDER_ROW && sourceScenarioId == targetScenarioId

  const shiftTablesToReload: EntityId[] = []
  let payload: ProcessDropOnShiftPayload | null = null

  if (sourceType === GridTableDragType.SHIFT_STOP_ROW) {
    const sourceStopIds = sourceRowData.map((row) => row.id)

    shiftTablesToReload.push(sourceShiftId)
    shiftTablesToReload.push(targetShiftId)

    payload = {
      orderIds: null,
      sourceShiftId,
      shiftId: targetShiftId,
      stopId: null,
      stopIds: sourceStopIds,
    }
  } else if (sourceType === GridTableDragType.INBOX_ORDER_ROW) {
    const orderIds = sourceRowData.map((row) => row.orderId)

    shiftTablesToReload.push(targetShiftId)

    let i = 0

    for (i = 0; i < sourceRowData.length; i++) {
      const row = sourceRowData[i]
      const assignedToScenario = (row.scenarios as AssignedToScenario[]).find(sc => sc.scenarioId === targetScenarioId)

      if (assignedToScenario) {
        shiftTablesToReload.push(assignedToScenario.shiftId)
      }
    }
    
    payload = {
      orderIds,
      sourceShiftId: null,
      shiftId: targetShiftId,
      stopId: null,
      stopIds: null,
    }
  } else if (sourceType === GridTableDragType.UNASSIGNED_ORDER_ROW) {
    const orderIds = sourceRowData.map((row) => row.orderId)

    shiftTablesToReload.push(targetShiftId)

    payload = {
      orderIds,
      sourceShiftId: null,
      shiftId: targetShiftId,
      stopId: null,
      stopIds: null,
    }
  }

  if (!payload) return
  
  // Set loading state
  shiftTablesToReload.forEach(shiftId => {
    dispatch(setPlanningShiftTableLoading({ id: shiftId, isLoading: true }))
  })

  if (refreshUnassignedOrders) {
    dispatch(setPlanningUnassignedTableLoading({ id: sourceRowData[0].scenarioId, isLoading: true }))
  }

  // We only need to update the target shift table state on planning screen
  //    because we are copying the orders from source shift to target shift
  await dispatch(processDropOnShift(payload))
  await dispatch(fetchScenarios({ scenarioIds: [targetScenarioId, sourceScenarioId] }))

  // Refresh the relevant tables
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const shiftTablesReloadActions: any = shiftTablesToReload.map(shiftId => dispatch(updatePlanningShiftTableState({ shiftId: shiftId })))

  if (refreshUnassignedOrders) {
    shiftTablesReloadActions.push(dispatch(updatePlanningUnassignedTableState({ scenarioId: sourceRowData[0].scenarioId })))
  }

  await Promise.allSettled(shiftTablesReloadActions)

  // Reset loading state
  shiftTablesToReload.forEach(shiftId => {
    dispatch(setPlanningShiftTableLoading({ id: shiftId, isLoading: false }))
  })

  if (refreshUnassignedOrders) {
    dispatch(setPlanningUnassignedTableLoading({ id: sourceRowData[0].scenarioId, isLoading: false }))
  }
}

export const processRunsheetDropOnUnassignedTreeItem = async ({ dispatch, item, treeItem }: ProcessDropOnTreeItemArgs) => {
  if (treeItem.widgetType !== TREE_WIDGETS.RunsheetUnassignedOrders) return

  const runsheetId = treeItem.runsheetId

  if (!runsheetId) return

  if (item.type === GridTableDragType.SHIFT_STOP_ROW) {
    // Receive orders from shift
    const orderIds = item.rows.map((stop) => stop.orderId)
    const sourceRunsheetId = item.rows[0].runsheetId
    const isSameRunsheet = sourceRunsheetId == runsheetId
    const shiftId = item.rows[0].shiftId

    // Set Loading State
    dispatch(setRunsheetShiftTableLoading({ id: shiftId, isLoading: true }))

    // Process Drop
    if (isSameRunsheet) {
      dispatch(setRunsheetUnassignedTableLoading({ id: runsheetId, isLoading: true }))
      await dispatch(processUnassignOrder({ shiftId, orderIds }))
    } else {
      await dispatch(processAssignToRunsheet({ sourceRunsheetId, runsheetId, orderIds }))
    }

    // Fetch runsheets and update table data
    const runsheetsToFetch = isSameRunsheet ? [runsheetId] : [runsheetId, sourceRunsheetId]

    await dispatch(fetchRunsheets({ runsheetIds: runsheetsToFetch }))
    await Promise.allSettled([
      dispatch(updateRunsheetShiftTableState({ shiftId: shiftId })),
      dispatch(updateRunsheetUnassignedTableState({ runsheetId: runsheetId })),
      isSameRunsheet ? Promise.resolve() : dispatch(updateRunsheetUnassignedTableState({ runsheetId: sourceRunsheetId })),
    ])

    // Reset Loading State
    dispatch(setRunsheetShiftTableLoading({ id: shiftId, isLoading: false }))
    dispatch(setRunsheetUnassignedTableLoading({ id: runsheetId, isLoading: false }))
  } else if (item.type === GridTableDragType.UNASSIGNED_ORDER_ROW) {
    const orderIds = item.rows.map((row) => row.orderId)
    const sourceRunsheetId = item.rows[0].runsheetId

    // Set Loading State
    dispatch(setRunsheetUnassignedTableLoading({ id: runsheetId, isLoading: true }))
    dispatch(setRunsheetUnassignedTableLoading({ id: sourceRunsheetId, isLoading: true }))

    await dispatch(processAssignToRunsheet({ sourceRunsheetId, runsheetId, orderIds }))
    await dispatch(fetchRunsheets({ runsheetIds: [sourceRunsheetId, runsheetId] }))
    await Promise.allSettled([
      dispatch(updateRunsheetUnassignedTableState({ runsheetId: runsheetId })),
      dispatch(updateRunsheetUnassignedTableState({ runsheetId: sourceRunsheetId })),
    ])

    // Reset Loading State
    dispatch(setRunsheetUnassignedTableLoading({ id: runsheetId, isLoading: false }))
    dispatch(setRunsheetUnassignedTableLoading({ id: sourceRunsheetId, isLoading: false }))
  }
}

export const processRunsheetDropOnShiftTreeItem = async ({ dispatch, item, treeItem }: ProcessDropOnTreeItemArgs) => {
  if (treeItem.widgetType !== TREE_WIDGETS.RunsheetShiftTable) return

  const sourceRowData = item.rows
  const sourceType = sourceRowData[0]?.dragType
  const sourceShiftId = sourceRowData[0].shiftId
  const sourceRunsheetId = sourceRowData[0].runsheetId
  const targetShiftId = treeItem.shiftId
  const targetRunsheetId = treeItem.runsheetId
  let refreshSourceUnassignedTable = false

  let payload: ProcessDropOnShiftPayload | null = null

  if (sourceType === GridTableDragType.SHIFT_STOP_ROW) {
    const sourceStopIds = sourceRowData.map((row) => row.id)

    payload = {
      orderIds: null,
      sourceShiftId,
      shiftId: targetShiftId,
      stopId: null,
      stopIds: sourceStopIds,
    }
  } else if (sourceType === GridTableDragType.UNASSIGNED_ORDER_ROW) {
    const orderIds = sourceRowData.map((row) => row.orderId)

    refreshSourceUnassignedTable = true

    payload = {
      orderIds,
      sourceShiftId: null,
      shiftId: targetShiftId,
      stopId: null,
      stopIds: null,
    }
  }

  if (payload !== null) {
    // Set loading state
    dispatch(setRunsheetShiftTableLoading({ id: targetShiftId, isLoading: true }))

    if (payload.sourceShiftId) {
      dispatch(setRunsheetShiftTableLoading({ id: payload.sourceShiftId, isLoading: true }))
    } else if (refreshSourceUnassignedTable) {
      dispatch(setRunsheetUnassignedTableLoading({ id: sourceRunsheetId, isLoading: true }))
    }

    // We only need to update the target shift table state on planning screen
    //    because we are copying the orders from source shift to target shift
    await dispatch(processDropOnShift(payload))
    await dispatch(fetchRunsheets({ runsheetIds: [targetRunsheetId, sourceRunsheetId] }))
    await Promise.allSettled([
      dispatch(updateRunsheetShiftTableState({ shiftId: targetShiftId })),
      payload.sourceShiftId ? dispatch(updateRunsheetShiftTableState({ shiftId: payload.sourceShiftId })) : Promise.resolve(),
      refreshSourceUnassignedTable ? dispatch(updateRunsheetUnassignedTableState({ runsheetId: sourceRunsheetId })) : Promise.resolve(),
    ])

    // Reset loading state
    dispatch(setRunsheetShiftTableLoading({ id: targetShiftId, isLoading: false }))

    if (payload.sourceShiftId) {
      dispatch(setRunsheetShiftTableLoading({ id: payload.sourceShiftId, isLoading: false }))
    } else if (refreshSourceUnassignedTable) {
      dispatch(setRunsheetUnassignedTableLoading({ id: sourceRunsheetId, isLoading: false }))
    }
  }
}

